'use client';

import { c as _c } from "react/compiler-runtime";
import { createBackerRewardScope, type GetPositionsRow, useGetPositions, usePrefetchGetBackerRewards, usePrefetchGetPositions, usePrefetchGetProfilePortfolioSnapshot } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef, useState } from 'react';
import { PositionsEmpty } from '@/components/portfolio/empty-states/positions-empty';
import { isOwnViewer, usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { Skeleton } from '@/components/ui/app-skeleton';
import { InfiniteList } from '@/components/ui/infinite-list';
import { Panel, PanelContent } from '@/components/ui/panel';
import { USDC_DUST_THRESHOLD_D6 } from '@/config/solana';
import { onEvent } from '@/events';
import type { UserPortfolioPositionDisplayRow } from '@/hooks/positions/user-portfolio/display-types';
import { useUserPortfolioPositionRows } from '@/hooks/positions/user-portfolio/selectors';
import { hasPositiveSnapshotOutcomeBalance } from '@/lib/market/position-visibility';
import { PORTFOLIO_BACKED_PROPOSAL_FIELDS, PORTFOLIO_REWARD_FIELDS, POSITION_FIELDS } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { evaluatePortfolioPositionVisibility } from '../positions-panel/position-lifecycle-visibility';
import { PortfolioListTransition } from './portfolio-list-transition';
import { PortfolioPositionListItem } from './portfolio-position-list-item';
import { PortfolioPositionsControls, type PositionCategory } from './portfolio-positions-controls';
import { PortfolioProposalPositions } from './portfolio-proposal-positions';
import { PortfolioRewards, type PortfolioRewardsContent, type RewardView } from './portfolio-rewards';
type ActiveTab = 'open' | 'closed';
type PanelPositionRow = {
  readonly kind: 'open';
  readonly row: UserPortfolioPositionDisplayRow;
} | {
  readonly kind: 'closed';
  readonly row: GetPositionsRow;
};
export function PortfolioPositionsCard(t0) {
  const $ = _c(61);
  const {
    userId,
    financialQueryId,
    snapshotUserId,
    walletPubkeys
  } = t0;
  const prefetchPositions = usePrefetchGetPositions();
  const prefetchSnapshot = usePrefetchGetProfilePortfolioSnapshot();
  const prefetchBackers = usePrefetchGetBackerRewards();
  const t1 = usePortfolioContext();
  let t2;
  if ($[0] !== t1) {
    t2 = isOwnViewer(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isOwn = t2;
  const [activeTab, setActiveTab] = useState("open");
  const [rewardView, setRewardView] = useState("claimable");
  const [category, setCategory] = useState("markets");
  const warnedInvalidLifecycleRows = useRef(null);
  let t3;
  if ($[2] !== snapshotUserId || $[3] !== walletPubkeys) {
    t3 = {
      userId: snapshotUserId,
      wallets: walletPubkeys
    };
    $[2] = snapshotUserId;
    $[3] = walletPubkeys;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const openPositions = useUserPortfolioPositionRows(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [...POSITION_FIELDS];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== userId || $[7] !== walletPubkeys) {
    t5 = {
      userId,
      walletPubkeys,
      status: "closed",
      includeZero: "true",
      fields: t4
    };
    $[6] = userId;
    $[7] = walletPubkeys;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = category !== "proposals" && category !== "rewards" && activeTab === "closed" && userId.length > 0;
  let t7;
  if ($[9] !== t6) {
    t7 = {
      enabled: t6
    };
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const closedPositions = useGetPositions(t5, t7);
  const positionType = category === "winners" ? "winner" : "outcome";
  let t8;
  if ($[11] !== openPositions.rows || $[12] !== positionType) {
    let t9;
    if ($[14] !== positionType) {
      t9 = row => row.positionType === positionType && isOpenDisplayRow(row) ? [{
        kind: "open",
        row
      }] : [];
      $[14] = positionType;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    t8 = openPositions.rows.flatMap(t9).toSorted(_temp);
    $[11] = openPositions.rows;
    $[12] = positionType;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const openRows = t8;
  let t9;
  if ($[16] !== closedPositions.rows || $[17] !== positionType) {
    let t10;
    if ($[19] !== positionType) {
      t10 = row_0 => row_0.positionType === positionType && evaluatePortfolioPositionVisibility(row_0).isVisible ? [{
        kind: "closed",
        row: row_0
      }] : [];
      $[19] = positionType;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    t9 = closedPositions.rows.flatMap(t10).toSorted(_temp2);
    $[16] = closedPositions.rows;
    $[17] = positionType;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const closedRows = t9;
  const rows = activeTab === "open" ? openRows : closedRows;
  const isPending = activeTab === "open" ? openPositions.isPending : closedPositions.isPending;
  const isError = activeTab === "open" ? openPositions.isError : closedPositions.isError;
  let t10;
  let t11;
  if ($[21] !== closedPositions.rows) {
    t10 = () => {
      let warningKeys = warnedInvalidLifecycleRows.current;
      if (warningKeys === null) {
        warningKeys = new Set();
        warnedInvalidLifecycleRows.current = warningKeys;
      }
      for (const row_1 of closedPositions.rows) {
        const {
          invalidFields
        } = evaluatePortfolioPositionVisibility(row_1);
        if (invalidFields.length === 0) {
          continue;
        }
        const warningKey = [row_1.market, String(row_1.outcomeIndex), invalidFields.join(",")].join("|");
        if (warningKeys.has(warningKey)) {
          continue;
        }
        warningKeys.add(warningKey);
        logger.warn("portfolio.position_lifecycle_invalid", {
          invalidFields,
          resultState: row_1.resultState
        });
      }
    };
    t11 = [closedPositions.rows];
    $[21] = closedPositions.rows;
    $[22] = t10;
    $[23] = t11;
  } else {
    t10 = $[22];
    t11 = $[23];
  }
  useEffect(t10, t11);
  let t12;
  if ($[24] !== category) {
    t12 = function handleTabChange(next) {
      if (category === "rewards") {
        if (next === "claimable" || next === "claimed") {
          setRewardView(next);
        }
        return;
      }
      if (next !== "open" && next !== "closed") {
        return;
      }
      setActiveTab(next);
      onEvent("portfolio_positions_toggled", {
        to: next
      });
    };
    $[24] = category;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const handleTabChange = t12;
  let t13;
  if ($[26] !== activeTab || $[27] !== financialQueryId || $[28] !== isOwn || $[29] !== prefetchBackers || $[30] !== prefetchPositions || $[31] !== prefetchSnapshot || $[32] !== snapshotUserId || $[33] !== userId || $[34] !== walletPubkeys) {
    t13 = function prefetchCategory(next_0, t14) {
      const tab = t14 === undefined ? activeTab : t14;
      if (next_0 === "markets" || next_0 === "winners") {
        if (tab === "open") {
          prefetchSnapshot({
            userId: snapshotUserId
          });
        } else {
          prefetchPositions({
            userId,
            walletPubkeys,
            status: "closed",
            includeZero: "true",
            fields: [...POSITION_FIELDS]
          });
        }
      } else {
        if (next_0 === "proposals" && walletPubkeys.length > 0) {
          prefetchBackers({
            userId: financialQueryId,
            rewardScope: createBackerRewardScope(financialQueryId, walletPubkeys),
            walletPubkeysCsv: walletPubkeys.join(","),
            sortBy: "backed",
            fields: PORTFOLIO_BACKED_PROPOSAL_FIELDS
          });
        } else {
          if (next_0 === "rewards") {
            prefetchSnapshot({
              userId: snapshotUserId
            });
            for (const identity of isOwn ? [...new Set(walletPubkeys)] : [financialQueryId]) {
              if (identity.length === 0) {
                continue;
              }
              const wallets = isOwn ? [identity] : walletPubkeys;
              prefetchBackers({
                userId: identity,
                rewardScope: createBackerRewardScope(identity, wallets),
                walletPubkeysCsv: wallets.join(","),
                limit: "100",
                fields: PORTFOLIO_REWARD_FIELDS
              });
            }
          }
        }
      }
    };
    $[26] = activeTab;
    $[27] = financialQueryId;
    $[28] = isOwn;
    $[29] = prefetchBackers;
    $[30] = prefetchPositions;
    $[31] = prefetchSnapshot;
    $[32] = snapshotUserId;
    $[33] = userId;
    $[34] = walletPubkeys;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const prefetchCategory = t13;
  let t14;
  if ($[36] !== activeTab || $[37] !== category || $[38] !== handleTabChange || $[39] !== isOwn || $[40] !== prefetchCategory || $[41] !== rewardView) {
    t14 = function renderControls(count, rewardsCount, rewardsHasMore) {
      return <PortfolioPositionsControls tab={category === "rewards" ? rewardView : activeTab} category={category} count={category === "rewards" && !isOwn ? undefined : count} rewardsCount={rewardsCount} showRewardsCount={isOwn} rewardsHasMore={rewardsHasMore} countHasMore={category === "rewards" && rewardsHasMore} onTabChange={handleTabChange} onCategoryChange={setCategory} onCategoryHover={next_1 => {
        if (next_1 !== category) {
          prefetchCategory(next_1);
        }
      }} onTabHover={next_2 => {
        if (category === "rewards") {
          if (next_2 !== rewardView) {
            prefetchCategory(category);
          }
        } else {
          if (next_2 !== activeTab && (next_2 === "open" || next_2 === "closed")) {
            prefetchCategory(category, next_2);
          }
        }
      }} />;
    };
    $[36] = activeTab;
    $[37] = category;
    $[38] = handleTabChange;
    $[39] = isOwn;
    $[40] = prefetchCategory;
    $[41] = rewardView;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const renderControls = t14;
  let t15;
  if ($[43] !== activeTab || $[44] !== category || $[45] !== financialQueryId || $[46] !== isError || $[47] !== isOwn || $[48] !== isPending || $[49] !== renderControls || $[50] !== rewardView || $[51] !== rows || $[52] !== snapshotUserId || $[53] !== walletPubkeys) {
    t15 = t16 => {
      const {
        claimableCount,
        claimedCount,
        hasMore,
        renderItems
      } = t16;
      return <Panel data-testid="portfolio-positions-card"><PanelContent className="flex min-w-0 flex-col gap-4">{category === "rewards" ? <>{renderControls(rewardView === "claimable" ? claimableCount : claimedCount, claimableCount, hasMore)}<PortfolioListTransition view={`rewards:${rewardView}`}>{renderItems(rewardView)}</PortfolioListTransition></> : category === "proposals" ? <PortfolioProposalPositions userId={financialQueryId} creatorUserId={snapshotUserId} walletPubkeys={walletPubkeys} own={isOwn} tab={activeTab} renderControls={count_0 => renderControls(count_0, claimableCount, hasMore)} /> : <>{renderControls(isPending || isError ? undefined : rows.length, claimableCount, hasMore)}<PortfolioListTransition view={`${category}:${activeTab}`}><InfiniteList items={rows} getItemKey={_temp3} renderItem={item_0 => item_0.kind === "open" ? <PortfolioPositionListItem row={item_0.row} mode="open" isOwnPortfolio={isOwn} /> : <PortfolioPositionListItem row={item_0.row} mode="closed" isOwnPortfolio={isOwn} />} hasNextPage={false} isFetchingNextPage={false} fetchNextPage={noNextPage} scrollMode="page-below-lg" isError={isError} isInitialLoading={isPending} loadingPlaceholder={<div className="flex flex-col gap-2" data-testid="positions-loading-skeletons" aria-busy="true"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>} emptyState={<PositionsEmpty tab={activeTab} />} listClassName="flex flex-col gap-4 lg:gap-0" /></PortfolioListTransition></>}</PanelContent></Panel>;
    };
    $[43] = activeTab;
    $[44] = category;
    $[45] = financialQueryId;
    $[46] = isError;
    $[47] = isOwn;
    $[48] = isPending;
    $[49] = renderControls;
    $[50] = rewardView;
    $[51] = rows;
    $[52] = snapshotUserId;
    $[53] = walletPubkeys;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== financialQueryId || $[56] !== isOwn || $[57] !== snapshotUserId || $[58] !== t15 || $[59] !== walletPubkeys) {
    t16 = <PortfolioRewards userId={snapshotUserId} financialQueryId={financialQueryId} walletPubkeys={walletPubkeys} own={isOwn}>{t15}</PortfolioRewards>;
    $[55] = financialQueryId;
    $[56] = isOwn;
    $[57] = snapshotUserId;
    $[58] = t15;
    $[59] = walletPubkeys;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  return t16;
}
function _temp3(item) {
  return item.row.positionId;
}
function _temp2(left_0, right_0) {
  return compareClosedRows(left_0.row, right_0.row);
}
function _temp(left, right) {
  return compareOpenRows(left.row, right.row);
}
function isOpenDisplayRow(row: UserPortfolioPositionDisplayRow): boolean {
  if (!hasPositiveSnapshotOutcomeBalance(row)) return false;
  const currentValueD6 = row.displayMark.currentValueD6;
  return currentValueD6 === null || currentValueD6 >= BigInt(USDC_DUST_THRESHOLD_D6);
}
function compareOpenRows(left: UserPortfolioPositionDisplayRow, right: UserPortfolioPositionDisplayRow): number {
  const leftValue = left.displayMark.currentValueD6;
  const rightValue = right.displayMark.currentValueD6;
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;
  if (leftValue === rightValue) return left.positionId.localeCompare(right.positionId);
  return leftValue > rightValue ? -1 : 1;
}
function compareClosedRows(left: GetPositionsRow, right: GetPositionsRow): number {
  const leftValue = signedWire(left.realizedPnlD6);
  const rightValue = signedWire(right.realizedPnlD6);
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;
  if (leftValue === rightValue) return left.positionId.localeCompare(right.positionId);
  return leftValue > rightValue ? -1 : 1;
}
function signedWire(value: string): bigint | null {
  return /^-?(?:0|[1-9]\d*)$/.test(value) ? BigInt(value) : null;
}
function noNextPage(): void {}